import { db } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  query,
  where,
  getDocs,
  Timestamp,
  deleteDoc
} from 'firebase/firestore';

/**
 * CAPTCHA Service - Prevents spam and automated abuse
 * Uses Google reCAPTCHA v3 for invisible verification
 * Implements rate limiting and suspicious activity tracking
 */

const COLLECTIONS = {
  CAPTCHA_LOGS: 'captchaLogs',
  RATE_LIMITS: 'rateLimits',
  BLOCKED_IPS: 'blockedIPs'
};

// Rate limit configurations
const RATE_LIMITS = {
  REGISTRATION: { max: 5, windowMs: 3600000 }, // 5 registrations per hour
  CHALLENGE_SUBMISSION: { max: 50, windowMs: 3600000 }, // 50 submissions per hour
  CHAT_MESSAGE: { max: 100, windowMs: 3600000 }, // 100 messages per hour
  HOMEWORK_SUBMISSION: { max: 10, windowMs: 3600000 }, // 10 homework submissions per hour
  PET_INTERACTION: { max: 30, windowMs: 3600000 } // 30 pet interactions per hour
};

// reCAPTCHA v3 configuration
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // Test key

// Development: Disable CAPTCHA (useful for testing)
const DISABLE_CAPTCHA = process.env.REACT_APP_DISABLE_CAPTCHA === 'true';

// Production mode: Use backend verification
const USE_BACKEND_VERIFICATION = process.env.REACT_APP_USE_BACKEND_CAPTCHA !== 'false'; // Default to true

// Minimum score for reCAPTCHA v3 (0.0 - 1.0, higher is better)
const MIN_SCORE = parseFloat(process.env.REACT_APP_RECAPTCHA_MIN_SCORE || '0.5');

// Cloud Function reference for backend verification (lazy initialization)
let verifyRecaptchaFunction = null;

const getVerifyRecaptchaFunction = () => {
  if (!verifyRecaptchaFunction && functions) {
    try {
      verifyRecaptchaFunction = httpsCallable(functions, 'verifyRecaptcha');
    } catch (error) {
      console.warn('⚠️ Could not initialize verifyRecaptcha function:', error);
    }
  }
  return verifyRecaptchaFunction;
};

// ==================== reCAPTCHA Integration ====================

/**
 * Load reCAPTCHA script
 */
export const loadReCaptcha = () => {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha) {
      resolve(window.grecaptcha);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.onload = () => {
      window.grecaptcha.ready(() => {
        resolve(window.grecaptcha);
      });
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

/**
 * Execute reCAPTCHA and get token
 */
export const executeReCaptcha = async (action) => {
  try {
    await loadReCaptcha();
    
    return new Promise((resolve, reject) => {
      window.grecaptcha.ready(async () => {
        try {
          const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
          resolve(token);
        } catch (error) {
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error('Error executing reCAPTCHA:', error);
    throw new Error('CAPTCHA verification failed');
  }
};

/**
 * Verify reCAPTCHA token
 * Uses backend Cloud Function for production, falls back to client-side for development
 */
export const verifyReCaptcha = async (token, action, userId = null, ipAddress = null) => {
  try {
    if (!token || token.length < 10) {
      throw new Error('Invalid CAPTCHA token');
    }

    // Use backend verification in production
    const verifyFunction = getVerifyRecaptchaFunction();
    if (USE_BACKEND_VERIFICATION && verifyFunction) {
      try {
        console.log('🔒 Verifying reCAPTCHA via backend...');
        const result = await verifyFunction({ token, action });
        const verificationData = result.data;

        if (!verificationData.success) {
          throw new Error('CAPTCHA verification failed');
        }

        // Log successful verification
        await logCaptchaAttempt(action, userId, ipAddress, true, verificationData.score);

        return {
          success: true,
          score: verificationData.score,
          action: verificationData.action,
          hostname: verificationData.hostname,
          timestamp: new Date()
        };
      } catch (backendError) {
        console.error('❌ Backend verification failed:', backendError);
        
        // If backend is not available, fall back to client-side (development only)
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ Falling back to client-side verification (development mode)');
          await logCaptchaAttempt(action, userId, ipAddress, true, 0.9);
          return {
            success: true,
            score: 0.9,
            action,
            timestamp: new Date(),
            fallback: true
          };
        }
        
        throw backendError;
      }
    } else {
      // Client-side verification (development/fallback)
      console.warn('⚠️ Using client-side CAPTCHA verification (not recommended for production)');
      
      // Log CAPTCHA attempt
      await logCaptchaAttempt(action, userId, ipAddress, true, 0.9);
      
      return {
        success: true,
        score: 0.9,
        action,
        timestamp: new Date(),
        fallback: true
      };
    }
  } catch (error) {
    console.error('Error verifying reCAPTCHA:', error);
    await logCaptchaAttempt(action, userId, ipAddress, false, 0);
    throw error;
  }
};

// ==================== Rate Limiting ====================

/**
 * Check if action is rate limited
 */
export const checkRateLimit = async (userId, action) => {
  try {
    const config = RATE_LIMITS[action];
    if (!config) {
      return { allowed: true };
    }
    
    const rateLimitRef = doc(db, COLLECTIONS.RATE_LIMITS, `${userId}_${action}`);
    const snapshot = await getDoc(rateLimitRef);
    
    const now = Date.now();
    const windowStart = now - config.windowMs;
    
    if (!snapshot.exists()) {
      // First action - allow and create record
      await setDoc(rateLimitRef, {
        userId,
        action,
        attempts: [now],
        createdAt: Timestamp.now()
      });
      return { allowed: true, remaining: config.max - 1 };
    }
    
    const data = snapshot.data();
    const recentAttempts = (data.attempts || []).filter(timestamp => timestamp > windowStart);
    
    if (recentAttempts.length >= config.max) {
      // Rate limit exceeded
      return {
        allowed: false,
        remaining: 0,
        resetAt: new Date(recentAttempts[0] + config.windowMs),
        message: `Rate limit exceeded. Try again in ${Math.ceil((recentAttempts[0] + config.windowMs - now) / 60000)} minutes.`
      };
    }
    
    // Update attempts
    await setDoc(rateLimitRef, {
      userId,
      action,
      attempts: [...recentAttempts, now],
      updatedAt: Timestamp.now()
    });
    
    return {
      allowed: true,
      remaining: config.max - recentAttempts.length - 1
    };
  } catch (error) {
    console.error('Error checking rate limit:', error);
    // On error, allow the action but log it
    return { allowed: true, error: true };
  }
};

/**
 * Reset rate limit for a user and action
 */
export const resetRateLimit = async (userId, action) => {
  try {
    const rateLimitRef = doc(db, COLLECTIONS.RATE_LIMITS, `${userId}_${action}`);
    await deleteDoc(rateLimitRef);
  } catch (error) {
    console.error('Error resetting rate limit:', error);
    throw error;
  }
};

/**
 * Get rate limit status for a user
 */
export const getRateLimitStatus = async (userId) => {
  try {
    const rateLimitsRef = collection(db, COLLECTIONS.RATE_LIMITS);
    const q = query(rateLimitsRef, where('userId', '==', userId));
    const snapshot = await getDocs(q);
    
    const status = {};
    const now = Date.now();
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const config = RATE_LIMITS[data.action];
      if (!config) return;
      
      const windowStart = now - config.windowMs;
      const recentAttempts = (data.attempts || []).filter(timestamp => timestamp > windowStart);
      
      status[data.action] = {
        current: recentAttempts.length,
        max: config.max,
        remaining: config.max - recentAttempts.length,
        resetAt: recentAttempts.length > 0 ? new Date(recentAttempts[0] + config.windowMs) : null
      };
    });
    
    return status;
  } catch (error) {
    console.error('Error getting rate limit status:', error);
    return {};
  }
};

// ==================== IP Blocking ====================

/**
 * Check if IP is blocked
 */
export const isIPBlocked = async (ipAddress) => {
  try {
    if (!ipAddress) return false;
    
    const blockedRef = doc(db, COLLECTIONS.BLOCKED_IPS, ipAddress);
    const snapshot = await getDoc(blockedRef);
    
    if (!snapshot.exists()) return false;
    
    const data = snapshot.data();
    
    // Check if block has expired
    if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
      await deleteDoc(blockedRef);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error checking IP block:', error);
    return false;
  }
};

/**
 * Block an IP address
 */
export const blockIP = async (ipAddress, reason, durationMs = 86400000) => {
  try {
    const blockedRef = doc(db, COLLECTIONS.BLOCKED_IPS, ipAddress);
    await setDoc(blockedRef, {
      ipAddress,
      reason,
      blockedAt: Timestamp.now(),
      expiresAt: Timestamp.fromDate(new Date(Date.now() + durationMs))
    });
  } catch (error) {
    console.error('Error blocking IP:', error);
    throw error;
  }
};

/**
 * Unblock an IP address
 */
export const unblockIP = async (ipAddress) => {
  try {
    const blockedRef = doc(db, COLLECTIONS.BLOCKED_IPS, ipAddress);
    await deleteDoc(blockedRef);
  } catch (error) {
    console.error('Error unblocking IP:', error);
    throw error;
  }
};

// ==================== CAPTCHA Logging ====================

/**
 * Log CAPTCHA attempt
 */
const logCaptchaAttempt = async (action, userId, ipAddress, success, score) => {
  try {
    const logsRef = collection(db, COLLECTIONS.CAPTCHA_LOGS);
    await setDoc(doc(logsRef), {
      action,
      userId: userId || 'anonymous',
      ipAddress: ipAddress || 'unknown',
      success,
      score,
      timestamp: Timestamp.now()
    });
  } catch (error) {
    console.error('Error logging CAPTCHA attempt:', error);
    // Don't throw - logging is non-critical
  }
};

/**
 * Get CAPTCHA logs for analysis
 */
export const getCaptchaLogs = async (filters = {}) => {
  try {
    const logsRef = collection(db, COLLECTIONS.CAPTCHA_LOGS);
    let q = query(logsRef);
    
    if (filters.userId) {
      q = query(q, where('userId', '==', filters.userId));
    }
    
    if (filters.action) {
      q = query(q, where('action', '==', filters.action));
    }
    
    if (filters.success !== undefined) {
      q = query(q, where('success', '==', filters.success));
    }
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting CAPTCHA logs:', error);
    return [];
  }
};

// ==================== Suspicious Activity Detection ====================

/**
 * Detect suspicious activity patterns
 */
export const detectSuspiciousActivity = async (userId, ipAddress) => {
  try {
    const logs = await getCaptchaLogs({ userId });
    
    if (logs.length < 5) return { suspicious: false };
    
    // Check for high failure rate
    const recentLogs = logs.slice(0, 10);
    const failureRate = recentLogs.filter(log => !log.success).length / recentLogs.length;
    
    if (failureRate > 0.7) {
      return {
        suspicious: true,
        reason: 'High CAPTCHA failure rate',
        severity: 'high'
      };
    }
    
    // Check for rapid requests
    const timestamps = recentLogs.map(log => log.timestamp.toMillis());
    const avgInterval = (Math.max(...timestamps) - Math.min(...timestamps)) / timestamps.length;
    
    if (avgInterval < 1000) { // Less than 1 second between requests
      return {
        suspicious: true,
        reason: 'Unusually rapid requests',
        severity: 'high'
      };
    }
    
    return { suspicious: false };
  } catch (error) {
    console.error('Error detecting suspicious activity:', error);
    return { suspicious: false, error: true };
  }
};

// ==================== Combined Verification ====================

/**
 * Complete verification with CAPTCHA and rate limiting
 */
export const verifyAction = async (action, userId, options = {}) => {
  try {
    // DEVELOPMENT MODE: Skip CAPTCHA if disabled
    if (DISABLE_CAPTCHA) {
      console.log('⚠️ CAPTCHA disabled in development mode');
      return {
        success: true,
        score: 1.0,
        rateLimitRemaining: 999,
        devMode: true
      };
    }
    
    // 1. Check if IP is blocked (if IP provided)
    if (options.ipAddress) {
      const blocked = await isIPBlocked(options.ipAddress);
      if (blocked) {
        throw new Error('Your IP address has been temporarily blocked');
      }
    }
    
    // 2. Check rate limit
    const rateLimit = await checkRateLimit(userId, action);
    if (!rateLimit.allowed) {
      throw new Error(rateLimit.message || 'Rate limit exceeded');
    }
    
    // 3. Execute reCAPTCHA
    const token = await executeReCaptcha(action);
    
    // 4. Verify CAPTCHA
    const verification = await verifyReCaptcha(token, action, userId, options.ipAddress);
    
    if (!verification.success || verification.score < MIN_SCORE) {
      throw new Error('CAPTCHA verification failed');
    }
    
    // 5. Check for suspicious activity
    if (options.checkSuspicious) {
      const suspicious = await detectSuspiciousActivity(userId, options.ipAddress);
      if (suspicious.suspicious && suspicious.severity === 'high') {
        // Log but don't block yet
        console.warn('Suspicious activity detected:', suspicious);
      }
    }
    
    return {
      success: true,
      score: verification.score,
      rateLimitRemaining: rateLimit.remaining
    };
  } catch (error) {
    console.error('Verification failed:', error);
    throw error;
  }
};

const captchaService = {
  // reCAPTCHA
  loadReCaptcha,
  executeReCaptcha,
  verifyReCaptcha,
  
  // Rate Limiting
  checkRateLimit,
  resetRateLimit,
  getRateLimitStatus,
  
  // IP Blocking
  isIPBlocked,
  blockIP,
  unblockIP,
  
  // Logging
  getCaptchaLogs,
  
  // Detection
  detectSuspiciousActivity,
  
  // Combined
  verifyAction
};

export default captchaService;
